<template>
	<div :class="{'user-connected': loggedIn && !showMonthlyTop, 'user-not-connected': loggedIn === false || showMonthlyTop, 'landing-page': loggedIn === false || showMonthlyTop}" style="height: 100%;">
		<div v-if="loaderContent=='default'" class="global-loader">
			<loader />
		</div>

		<div v-if="loaderContent=='initial'" class="global-loader">
			<div>
				<div class="spinner-text">
					{{ t.initialLoadText }}
				</div>
				<loader />
			</div>
		</div>

		<div v-if="loaderContent=='error'" class="general-error">
			<div class="container">
				{{ t.generalErrorTryLater }}
				<!-- <div class="">
					<button type="button" class="btn-error btn-danger" @click="window.close();" name="button">{{t.generalErrorLogoutButton}}</button>
				</div> -->
			</div>
		</div>

		<div v-show="loaderContent==false" class="page-wrapper">
			<header-block />

			<div :class="{'container': !showMonthlyTop && !isMobile, 'container-fluid': showMonthlyTop || isMobile}" class="flex-grow-1">
				<div v-if="loggedIn !== undefined" :class="{ 'row': loggedIn !== false || showMonthlyTop }" style="position: relative;" class="h-100">
					<div
						:class="{
							'col-md-8 page-main-block': loggedIn && !showMonthlyTop,
							'sign-in-landing': loggedIn === false && !showMonthlyTop,
							'champion-monthly-landing': showMonthlyTop
						}" class="h-100"
					>
						<router-view :key="$route.path" v-slot="{ Component }">
							<transition name="fade" mode="out-in" appear>
								<keep-alive>
									<component :is="Component" />
								</keep-alive>
							</transition>
						</router-view>
					</div>
					
					<!-- Check if we're not in mobile version -->
					<right-panel v-if="loggedIn && !isMobile && !showMonthlyTop" />
				</div>
			</div>

			<footer-block v-if="loggedIn === false || settings.ui.footerForceBottomOnDesktop" />
		</div>

		<!-- This is a wrapper for playing sounds -->
		<audio ref="audio" />

		<ToastWrapper />
		<PopupWrapper />
	</div>
</template>

<script setup>
import { ref, watch, onMounted, computed, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { event } from 'vue-gtag'

import { initializeFacebook, loadFriends } from '../includes/facebook'
import HeaderBlock from './blocks/header-block.vue'
import RightPanel from './blocks/right-panel.vue'
import FooterBlock from './footer-block.vue'
import * as Cookies from 'tiny-cookie'

import {
	loadUserData,
	updateUserPoints,
} from '../classes/user'

import * as Push from '../includes/service-worker/notifications'
import { useWindowSize } from '@vueuse/core'
import { redirectToEntrance } from '../includes/shared'
import { loadOwnerData } from '../includes/owner'
import { loadRewards } from '../includes/rewards'
import ToastWrapper from './blocks/toast-wrapper.vue'
import PopupWrapper from './blocks/modal-wrapper.vue'
import { openAskNameModal, openGenericModal, openWelcomeModal } from '../includes/modals'
import { $emit, $on } from '../includes/event-bus'
import { useStore } from '@/js/stores/index';
import { initSounds } from '../includes/sounds'
import { useUserStore } from '../stores/user'
import { useNewsStore } from '../stores/news'

const axios = inject('axios')
const store = useStore()
const userStore = useUserStore()
const { loadUnreadNewsCount } = useNewsStore()

// Load owner information
loadOwnerData()

const { width } = useWindowSize()

// Router
const router = useRouter()
const route = useRoute()

// Refs
const audio = ref()

// // Register a global shortcut to this component
// window.$main = getCurrentInstance()

// Computeds
const currentLanguage = computed(() => store.currentLanguage)
const loggedIn = computed(() => userStore.loggedIn)
const loaderContent = computed(() => store.loaderContent)
const userLoaded = computed(() => userStore.userLoaded)
const userHash = computed(() => userStore.userHash)
const firstName = computed(() => userStore.userFirstName)
const lastName = computed(() => userStore.userLastName)
const settings = computed(() => store.settings)
const showMonthlyTop = computed(() => route.name == 'monthly-top-user')
const isMobile = computed(() => store.isMobile)
const t = computed(() => store.translations)

// Watchers
// We need to wait until user data is loaded to continue drawing the page
watch(userLoaded, () => {
	initialize()
})

watch(width, (width) => {
	if(width < 992){ // This is bootstrap "md" breakpoint's maximum value. Narrower screen we consider as "mobile"
		store.isMobile = true
	}else{
		store.isMobile = false
	}
}, { immediate: true }) // This is needed to force immediate update isMobile on page load

// Functions
async function initialize(){
	// Load menu items based on the current language
	loadMenuItems()
	
	// Setup facebook if enabled
	if(settings.value.fbAppId){
		initializeFacebook(settings.value.fbAppId, settings.value.fbApiVersion)
	}

	// We stop here if the user is not logged in
	if(!loggedIn.value){
		store.loaderContent = false
		return;
	}
	
	// Load all the stuff
	loadRewards()
	loadFriends()
	updateUserPoints()

	// Commenting as we will have this data loaded already
	checkNameAndAskIfNone()

	// Delay loading news information for 5 sec. It's not urgent
	if(settings.value.newsEnabled && loggedIn.value){
		setTimeout(loadUnreadNewsCount, 5000)
	}
	
	// Disable loader
	store.loaderContent = false
}

async function loadMenuItems(){
	// Get menu items
	const { data } = await axios.get('/api/user-side/getMenuItems?lang=' + currentLanguage.value)
	store.menuItems = data
}

async function checkUserSession(){
	if(!loggedIn.value) return; // Don't check user session if we're not even logged in.

	let data; 
	try {
		data = (await axios.get('/api/user-side/checkUserSession')).data
	} catch {
		$emit("close-generic-popup") // Close all popups if opened
		redirectToEntrance()
		return;
	}

	// Throw out user if session is expired
	if(data <= 0 || typeof data == 'undefined'){
		$emit("close-generic-popup") // Close all popups if opened
		redirectToEntrance()
		return;
	}

	// Show notification if time is less than 5 minutes
	if(data <= 300){
		openGenericModal('<div class="modal-title">' + t.value.sessionExpireNotification + '</div>', {
			buttonText: t.value.sessionExpireContinueButton,
			buttonClass: 'btn-success',
			callback: () => {
				axios.get('/api/touchSession')
			}
		})
	}
}

async function processLogin(params)
{
	const redirectTo = route.query?.redirectTo // Save this as soon as possible, since it could be cleaned up in parallel thread
	var callNumber = 0
	
	var call = async () => {
		if(callNumber > 60) return; // 60 sec timeout

		// check if userdata is loaded
		try {
			await axios.get('/api/user-side/getUserInitializationStatus')
		} catch (error) {
			if(error.response?.data?.error_code == "UserNotInitializedYet"){ // In progress. Let's wait.
				callNumber++;
				setTimeout(call, 1000) // Run it every second
			}else{ // Server returns something else. It's an unexpected problem
				// Show error popup
				store.loaderContent = "error"

				throw new Error("User initialization failed for user with error " + error.response.data, {
					cause: error
				})
			}

			return;
		}

		// Load initial user settings
		await loadUserData()
		
		// If set do a router redirect otherwise throw to dashboard
		router.replace({
			name: (params && params.redirect_route_name) ? params.redirect_route_name : "dashboard"
		})

		userStore.loggedIn = true

		checkNameAndAskIfNone()
		updateUserPoints()
		loadFriends()
		loadUnreadNewsCount()
		loadRewards()

		// If we have redirectTo parameter in query, navigate the user there
		if(redirectTo){
			router.replace(decodeURIComponent(redirectTo))
		}
		
		// Reset optimization cookie that user is not registered, it is used in widget
		Cookies.remove('user-not-registered.' + userHash.value)
		
		// Check if we should show a welcome screen to the user
		if(params && params.showWelcomeScreen){
			openWelcomeModal()
		}
		
		setTimeout(() => store.loaderContent = false, 1000)
	}

	await call()
}

/**
 * Check if the current user has name entered, and open a modal to enter, unless name change is not enabled
 */
function checkNameAndAskIfNone(){
	// Don't open a modal if other modal is opened, user has both names, or name change is forbidden
	if(!settings.value.user_name_change_enabled
		|| (firstName.value && lastName.value)
		|| store.genericModalOpened) return;

	openAskNameModal()
}

function enablePushNotifications(){
	if(!loggedIn.value){ // Let subscribe logged in users only
		return;
	}

	Push.enablePushNotifications()
}

// Lifecycle
onMounted(() => {
	// If the user is already loaded, the watch `userLoaded` will not get triggered, so we need to start it manually
	if(userLoaded.value){
		initialize()
	}

	// Push setup
	store.isPushSetUp = Push.isPushSetUp()
	store.isPushDenied = Push.isPushDenied()
	store.isPushNotDenied = Push.isPushNotDenied()
	store.isPushGranted = Push.isGranted()

	// Push related actions
	if(Push.isGranted()){ // If push is granted, enable it on the server. Maybe it has lost token or something.
		setTimeout(enablePushNotifications, 10000)
	}

	// Launch session expiration check (do every minute)
	setInterval(checkUserSession, 60000)

	// Initialize sounds
	initSounds(audio.value)
})

// Events
$on('push-notifications-changed', () => {
	store.isPushSetUp = Push.isPushSetUp()
	store.isPushDenied = Push.isPushDenied()
	store.isPushNotDenied = Push.isPushNotDenied()
	store.isPushGranted = Push.isGranted()
})

// Global events bus
$on('reward-created', () => {
	loadRewards()
	updateUserPoints()
})

$on('challenge-completed', () => updateUserPoints())

$on('login-successful', () => {
	store.loaderContent = "default"

	processLogin()
})

$on('user-accepted-terms', params => {
	store.loaderContent = "initial"

	// Track registration event in Google analytics
	if(settings.value.googleAnalyticsTrackingId){
		event('sign-up', {
			'event_category': 'account',
			'event_label': 'sign-up',
			'value': 0
		})
	}

	// When terms are accepted it's a first login, so let's show a welcome screen
	params.showWelcomeScreen = true

	processLogin(params)
})
</script>